import { useState, useEffect, useContext } from "react";
import { observer } from "mobx-react-lite";
import { motion } from "framer-motion";
import { useCoreController } from "@/services/coreController";
import { analyticsService } from "@/services/analytics";
import { useAuth } from "@/hooks/useAuth";
import {
  LogOut,
  Ticket,
  Users,
  Delete,
  PlusCircle,
  MinusCircle,
  Loader2,
  X,
  TrendingUp,
  Gift,
  DollarSign,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Compte } from "@/types/app";
import { Notification } from "@/components/Notification";
import { AuthContext } from "@/contexts/authContext";
import { useMatchPrediction } from "@/hooks/useMatchPrediction";
import { AppConfig } from "@/config/AppConfig";

const AppConstants = {
  minAmount: 250,
  minWithdrawAmount: 900,
  maxAmount: 1000000,
};

const HomePage = observer(() => {
  const navigate = useNavigate();
  const coreController = useCoreController();
  const { signOutUser } = useAuth();
  const authContextNew = useContext(AuthContext);
  const newUser = authContextNew?.user;
  const [amount, setAmount] = useState<string>("250");
  const [bookmaker, setBookmaker] = useState<Compte>();
  const [selectedAccount, setSelectedAccount] = useState<Compte | null>(null);
  const [isLoadingBookmakers, setIsLoadingBookmakers] = useState<boolean>(true);
  const [isDepositLoading, setIsDepositLoading] = useState<boolean>(false);
  const [isWithdrawLoading, setIsWithdrawLoading] = useState<boolean>(false);
  const { commercialId } = useParams();
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    message: string;
    type: "error" | "success";
  }>({
    isVisible: false,
    message: "",
    type: "error",
  });

  // État pour la bannière de parrainage
  const [showReferralBanner, setShowReferralBanner] = useState<boolean>(true);

  // Hook pour gérer le widget de pronostic
  const { hasActiveMatch } = useMatchPrediction();

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà fermé la bannière
    const hasClosedBanner = localStorage.getItem("referralBannerClosed");
    if (hasClosedBanner === "true") {
      setShowReferralBanner(false);
    }
  }, []);
  useEffect(() => {
    // Load application settings GLOBALLY (like Flutter does in constructor)
    console.debug("🔄 Loading application settings...");
    coreController.loadApplicationSetting();
  }, [coreController]);

  useEffect(() => {
    if (commercialId) {
      coreController.setCommercialId(commercialId);
      console.debug("📍 Commercial ID set from URL:", commercialId);
    }
  }, [commercialId]);

  useEffect(() => {
    analyticsService.trackScreenView("home_page");

    // Set default account
    if (
      coreController.appSettingResponse.compte &&
      coreController.appSettingResponse.compte.length > 0
    ) {
      setSelectedAccount(coreController.appSettingResponse.compte[0]);
      coreController.setAccount(coreController.appSettingResponse.compte[0]);

      // Simulate loading time like Flutter
      setTimeout(() => {
        setIsLoadingBookmakers(false);
      }, 1500);
    } else {
      // If no accounts yet, keep loading
      const checkInterval = setInterval(() => {
        if (
          coreController.appSettingResponse.compte &&
          coreController.appSettingResponse.compte.length > 0
        ) {
          setSelectedAccount(coreController.appSettingResponse.compte[0]);
          coreController.setAccount(
            coreController.appSettingResponse.compte[0]
          );
          setTimeout(() => {
            setIsLoadingBookmakers(false);
          }, 2000);
          clearInterval(checkInterval);
        }
      }, 500);

      // Timeout after 10 seconds
      setTimeout(() => {
        setIsLoadingBookmakers(false);
        clearInterval(checkInterval);
      }, 10000);

      return () => clearInterval(checkInterval);
    }
  }, [coreController]);

  const showNotification = (
    message: string,
    type: "error" | "success" = "error"
  ) => {
    setNotification({
      isVisible: true,
      message,
      type,
    });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, isVisible: false }));
  };

  const handleNumberClick = (num: string) => {
    if (amount === "0" || amount === "") {
      setAmount(num);
    } else {
      const newAmount = amount + num;
      if (parseInt(newAmount) <= AppConstants.maxAmount) {
        setAmount(newAmount);
      }
    }
  };

  const handleDelete = () => {
    if (amount.length > 1) {
      setAmount(amount.slice(0, -1));
    } else {
      setAmount("0");
    }
  };

  const handleAccountSelect = (account: Compte) => {
    setSelectedAccount(account);
    coreController.setAccount(account);
  };

  const handleDeposit = async () => {
    const amountNum = parseInt(amount);
    if (amountNum < AppConstants.minAmount) {
      showNotification(
        `Le montant doit être supérieur à ${AppConstants.minAmount}`
      );
      return;
    }
    console.debug("selectedAccoungID", selectedAccount?.id);

    setIsDepositLoading(true);

    try {
      const canContinue = await coreController.checkBalanceForDeposit(
        amountNum,
        selectedAccount?.id || ""
      );

      if (canContinue) {
        navigate("/deposit", {
          state: { amount: amountNum, accountType: selectedAccount?.type },
        });
      } else {
        showNotification(
          "Dépôt momentanément indisponible. Veuillez réessayer plus tard.",
          "error"
        );
      }
    } catch (error) {
      showNotification(
        "Erreur lors de la vérification du solde. Veuillez réessayer.",
        "error"
      );
    } finally {
      setIsDepositLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amountNum = parseInt(amount);
    if (amountNum < AppConstants.minWithdrawAmount) {
      showNotification(
        `Le montant minimum pour un retrait est ${AppConstants.minWithdrawAmount} FCFA`
      );
      return;
    }

    setIsWithdrawLoading(true);

    try {
      const canContinue = await coreController.checkWithdraw(amountNum);

      if (canContinue) {
        navigate("/withdraw", {
          state: { amount: amountNum, accountType: selectedAccount?.type },
        });
      } else {
        showNotification(
          "Retrait momentanément indisponible. Veuillez réessayer plus tard.",
          "error"
        );
      }
    } catch (error) {
      showNotification(
        "Erreur lors de la vérification du retrait. Veuillez réessayer.",
        "error"
      );
    } finally {
      setIsWithdrawLoading(false);
    }
  };

  const handleSupport = () => {
    const whatsappUrl = coreController.whatsappLink;
    window.open(whatsappUrl, "_blank");
  };

  const handleCloseBanner = () => {
    setShowReferralBanner(false);
    localStorage.setItem("referralBannerClosed", "true");
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const formatAmount = (amt: string) => {
    return parseInt(amt || "0").toLocaleString("fr-FR");
  };

  const getDepositButtonColor = () => {
    if (selectedAccount?.type === "1XBET") {
      return "bg-blue-600 hover:bg-blue-700";
    } else if (selectedAccount?.type === "MELBET") {
      return "bg-yellow-500 hover:bg-yellow-600";
    } else if (selectedAccount?.type === "BETWINNER") {
      return "bg-green-600 hover:bg-green-700";
    }
      else if (selectedAccount?.type === "COLDBET") {
      return "bg-blue-700 hover:bg-blue-800";
    }
     else if (selectedAccount?.type === "BETANDYOU") {
      return "bg-yellow-700 hover:bg-black-800";
    }
    return "bg-[#003d99] hover:bg-[#002d7a]";
  };

  const getDepositBackgroundcolor = () => {
    if (selectedAccount?.type === "1XBET") {
      return " bg-gradient-to-b from-blue-400 to-white";
    } else if (selectedAccount?.type === "MELBET") {
      return "bg-gradient-to-b from-yellow-500 hover:bg-yellow-60 to-white";
    } else if (selectedAccount?.type === "BETWINNER") {
      return "bg-gradient-to-b from-green-600 to-white";
    }
     else if (selectedAccount?.type === "COLDBET") {
      return "bg-gradient-to-r  from-blue-700 to-green-300";
    }
      else if (selectedAccount?.type === "BETANDYOU") {
      return "bg-gradient-to-r from-yellow-700 to-yellow-300";
    }
    return "bg-gradient-to-b from-[#002366] to-[#001a4d]";
  };
  const getbannerBackgroundcolor = () => {
    if (selectedAccount?.type === "1XBET") {
      return " bg-gradient-to-r from-blue-500  to-blue-200";
    } else if (selectedAccount?.type === "MELBET") {
      return "bg-gradient-to-r from-yellow-600 hover:bg-yellow-60 to-yellow-200";
    } else if (selectedAccount?.type === "BETWINNER") {
      return "bg-gradient-to-r from-green-700 to-green-300";
    }
    else if (selectedAccount?.type === "COLDBET") {
      return "bg-gradient-to-r from-blue-700 to-green-300";
    }
    else if (selectedAccount?.type === "BETANDYOU") {
      return "bg-gradient-to-r from-black-700 to-yellow-300";
    }
    return "bg-gradient-to-b from-[#002366] to-[#001a4d]";
  };

  return (
    <div
      className={"min-h-screen flex flex-col " + getDepositBackgroundcolor()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex gap-2">
          {  !AppConfig.isPartnerApp && <button
            onClick={() => navigate("/pronostics")}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-full text-white text-sm font-semibold shadow-lg transition-all"
          >
            Pronostiquer
          </button>
}
          <button
            onClick={handleSupport}
            className="flex items-center gap-2 px-3 py-3 bg-[#25D366] hover:bg-[#128C7E] rounded-full text-white font-semibold shadow-lg transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            Support
          </button>
          {  AppConfig.isPartnerApp ? <></> : (newUser) ? (
            <div>
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full font-semibold shadow-lg transition-all"
              >
                <DollarSign className="w-4 h-4" />
                <span className="font-bold text-sm">Voir mes gains</span>
              </button>
              <button
                onClick={handleLogout}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="flex items-center gap-2 px-4 py-3 bg-white text-[#002366] hover:bg-gray-100 rounded-full font-semibold shadow-lg transition-all"
            >
              <span className="font-bold">Connexion</span>
            </button>
          )}
        </div>
      </div>

      {/* Bannière d'incitation au parrainage */}
      {(showReferralBanner && AppConfig.isPartnerApp == false) && (
        // && !newUser
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          className={
            "mx-4 mb-4 p-2.5 rounded-xl shadow-lg relative overflow-hidden " +
            getbannerBackgroundcolor()
          }
        >
          {/* Bouton pour fermer la bannière */}
          <button
            onClick={handleCloseBanner}
            className="absolute top-1.5 right-1.5 p-1 bg-white/10 hover:bg-white/20 rounded-full transition-all"
          >
            <X className="w-3.5 h-3.5 text-white" />
          </button>

          {/* Contenu de la bannière */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1">
              <div className="p-2 bg-white/10 rounded-full shrink-0">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-sm mb-0.5 flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 shrink-0" />
                  <span className="truncate">
                    Gagnez de l'argent avec vos amis !
                  </span>
                </h3>
                <p className="text-white text-xs">
                  Parrainez vos amis et gagnez de l'argent sur leurs recharge
                  via notre application
                </p>
              </div>
            </div>

            <div className="flex gap-1.5 shrink-0">
              <button
                onClick={() => {
                  navigate("/register");
                  analyticsService.trackEvent("go-register-refferal");

                  handleCloseBanner();
                }}
                className="px-3 py-1.5 bg-white text-[#002366] font-semibold rounded-full hover:bg-gray-100 transition-all shadow-md text-xs"
              >
                Commencer
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Loading State for Bookmakers */}
      {isLoadingBookmakers ? (
        <div className="flex-1 flex flex-col items-center justify-center p-5">
          <div className="flex gap-2 mb-8">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-4 h-4 rounded-full bg-white"
                animate={{
                  y: [0, -15, 0],
                }}
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: i * 0.15,
                }}
              />
            ))}
          </div>
          <p
            className="text-lg font-medium text-white mb-2"
            style={{ fontFamily: "Poppins, sans-serif" }}
          >
            Chargement de l'application...
          </p>
          <p
            className="text-sm text-white/70 text-center px-4"
            style={{ fontFamily: "Poppins, sans-serif" }}
          >
            Veuillez patienter pendant que nous chargeons les plateformes
            disponibles
          </p>
        </div>
      ) : (
        <>
          {/* Main Content */}
          <div className="flex-1 flex flex-col px-4">
            {/* Title */}
            <h1 className="text-white text-xl font-semibold text-center mb-4">
              Choisir la plateforme
            </h1>

            {/* Platform Selector */}
            <div className="flex flex-wrap gap-3 mb-6 items-center justify-center">
              {coreController.appSettingResponse.compte?.map((account) => {
              const getAccountStyle = () => {
                const isSelected = selectedAccount?.id === account.id;

                if (account.type === "1XBET") {
                return isSelected
                  ? "bg-blue-100 border-2 border-blue-500"
                  : "bg-white text-gray-800";
                } else if (account.type === "MELBET") {
                return isSelected
                  ? "bg-yellow-100 border-2 border-yellow-500"
                  : "bg-white text-gray-800";
                } else if (account.type === "BETWINNER") {
                return isSelected
                  ? "bg-green-100 border-2 border-green-800"
                  : "bg-white text-gray-800";
                }
                else if (account.type === "COLDBET") {
                return isSelected
                  ? "bg-green-100 border-2 border-green-800"
                  : "bg-white text-gray-800";
                }
                return isSelected
                ? "bg-white/90 border-2 border-[#002366]"
                : "bg-white text-gray-800";
              };

              return (
                <button
                key={account.id}
                onClick={() => handleAccountSelect(account)}
                className={`px-4 py-2.5 rounded-full font-bold text-xs whitespace-nowrap transition-all ${getAccountStyle()}`}
                style={{
                  fontFamily:
                  account.type === "1XBET"
                    ? "Arial, sans-serif"
                    : "inherit",
                }}
                >
                {account.type === "1XBET" && (
                  <span className="font-bold">1X</span>
                )}
                {account.type === "1XBET" && (
                  <span className="text-blue-600 font-bold">BET</span>
                )}
                {account.type === "MELBET" && (
                  <span className="font-black">MEL</span>
                )}
                {account.type === "MELBET" && (
                  <span className="text-yellow-500 font-black">BET</span>
                )}
                {account.type === "BETWINNER" && (
                  <span className="text-green-500 font-black">BET</span>
                )}
                {account.type === "BETWINNER" && (
                  <span className="font-black">WINNER</span>
                )}
                {account.type === "COLDBET" && (
                  <span className="text-green-500 font-black">COLD</span>
                )}
                {account.type === "COLDBET" && (
                  <span className="font-black">BET</span>
                )}
                  {account.type === "BETANDYOU" && (
                  <span className="text-yellow-500 font-black">BET</span>
                )}
                {account.type === "BETANDYOU" && (
                  <span className="text-black font-black">ANDYOU</span>
                )}
                 
                {!["1XBET", "MELBET", "BETWINNER", "COLDBET", "BETANDYOU"].includes(
                  account.type || ""
                ) && account.type}
                </button>
              );
              })}
            </div>

            {/* Amount Display */}
            <motion.div
              className="bg-white rounded-3xl p-2 shadow-xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <div className="text-center text-2xl font-bold text-gray-900 mb-2">
                {formatAmount(amount)} FCFA
              </div>

              {/* Number Pad */}
              <div className="grid grid-cols-3 gap-3 max-w-[285px] mx-auto">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <button
                    key={num}
                    onClick={() => handleNumberClick(num.toString())}
                    className="aspect-square rounded-2xl bg-gray-50 hover:bg-gray-100 text-3xl font-semibold text-gray-900 transition-all active:scale-95 shadow-sm"
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handleNumberClick("00")}
                  className="aspect-square rounded-2xl bg-gray-50 hover:bg-gray-100 text-3xl font-semibold text-gray-900 transition-all active:scale-95 shadow-sm"
                >
                  00
                </button>
                <button
                  onClick={() => handleNumberClick("0")}
                  className="aspect-square rounded-2xl bg-gray-50 hover:bg-gray-100 text-3xl font-semibold text-gray-900 transition-all active:scale-95 shadow-sm"
                >
                  0
                </button>
                <button
                  onClick={handleDelete}
                  className="aspect-square rounded-3xl bg-orange-100 hover:bg-orange-200 text-orange-600 transition-all active:scale-95 shadow-sm flex items-center justify-center"
                >
                  <Delete className="w-7 h-7" />
                </button>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <div className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleDeposit}
                  disabled={isDepositLoading}
                  className={`py-4 px-4 rounded-2xl ${getDepositButtonColor()} ${
                    isDepositLoading
                      ? "opacity-80 cursor-not-allowed"
                      : "hover:opacity-90"
                  } text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 disabled:scale-100`}
                >
                  {isDepositLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <PlusCircle className="w-6 h-6" />
                  )}
                  {isDepositLoading
                    ? "Vérification..."
                    : `Dépôt ${selectedAccount?.type}`}
                </button>
                <button
                  onClick={handleWithdraw}
                  disabled={isWithdrawLoading}
                  className={`py-4 px-4 rounded-2xl bg-[#22c55e] ${
                    isWithdrawLoading
                      ? "opacity-80 cursor-not-allowed"
                      : "hover:bg-[#16a34a]"
                  } text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 disabled:scale-100`}
                >
                  {isWithdrawLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <MinusCircle className="w-6 h-6" />
                  )}
                  {isWithdrawLoading
                    ? "Vérification..."
                    : `Retrait ${selectedAccount?.type}`}
                </button>
              </div>
              <div className="w-full ">
                {/* <button
                  onClick={() => navigate("/coupons")}
                  className="w-full py-3 px-4 rounded-2xl bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                >
                  <Ticket className="w-5 h-5" />
                  Coupons
                </button> */}
                {newUser && (
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                  >
                    <Users className="w-5 h-5" />
                    Affiliation
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Notification */}
          <Notification
            message={notification.message}
            type={notification.type}
            isVisible={notification.isVisible}
            onClose={hideNotification}
          />
        </>
      )}
    </div>
  );
});

export default HomePage;
